import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "hooks";
import { Controller, useForm, useWatch } from "react-hook-form";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Grid,
    Paper,
    Snackbar,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    FormControlLabel,
} from "@mui/material";
import {
    useLazyGetDriverAndVehicleDetailQuery,
    useLazySearchDriversWithVehiclesForTransferQuery,
    usePartialTransferVehicleShipmentsMutation,
} from "store/services/driverApi";
import {
    useLazyGetShipmentDetailByVehicleIdQuery,
} from "store/services/shipmentLoadingApi";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import AddVehicleModal from "./driverComponents/AddVehicleModal";

const INITIAL_STATE = { driver: null, vehicle: null };

// Sentinel — never stored as actual vehicle value
const ADD_VEHICLE_SENTINEL = { _id: "__ADD_VEHICLE__", __isAddVehicle__: true };

export const DriverPartialTransferScreen = () => {
    const debounceRef = useRef(null);
    const [toast, setToast] = useState({ open: false, message: "", severity: "success" });
    const [selectedLoadings, setSelectedLoadings] = useState(new Set());
    const [selectedShipments, setSelectedShipments] = useState(new Set());
    const [selectedToVehicle, setSelectedToVehicle] = useState(null);
    const [addVehicleModalOpen, setAddVehicleModalOpen] = useState(false);
    const [extraDriverVehicles, setExtraDriverVehicles] = useState([]);   // FIX 1

    const { loggedInUser, viewEntity } = useAuth();
    const driverIds = loggedInUser?.typeRef;

    const [fetchDrivers, { data: driverOptions, isLoading: isLoadingDrivers }] =
        useLazySearchDriversWithVehiclesForTransferQuery();
    const [fetchDriverVehicle, { data, isLoading }] =
        useLazyGetDriverAndVehicleDetailQuery();
    const [fetchShipmentDetails, { data: shipmentData, isLoading: isLoadingShipments }] =
        useLazyGetShipmentDetailByVehicleIdQuery();
    const [partialTransferShipments, { isLoading: isTransferring }] =
        usePartialTransferVehicleShipmentsMutation();

    const { control, reset } = useForm({ defaultValues: INITIAL_STATE, mode: "onChange" });

    const selectedDriver = useWatch({ control, name: "driver" });
    const selectedVehicle = useWatch({ control, name: "vehicle" });

    const getUnitDisplay = (shipment) => {
        if (shipment?.weight) return `${shipment.weight} kg`;
        if (shipment?.unit?.value && shipment?.unit?.unitType?.name) {
            return shipment.unit.unitType.name.toLowerCase() === "weight"
                ? `${shipment.unit.value} kg`
                : `${shipment.unit.value} ${shipment.unit.unitType.name}`;
        }
        return "N/A";
    };
    useEffect(() => {
        if (driverIds) fetchDriverVehicle(driverIds);
    }, [driverIds, fetchDriverVehicle]);

    useEffect(() => {
        if (data?.assigned_vehicle_ids?.length) {
            reset({ vehicle: data.assigned_vehicle_ids[0] });
        }
    }, [data, reset]);

    useEffect(() => {
        if (selectedVehicle?._id) {
            fetchShipmentDetails({ vehicleId: selectedVehicle._id });
            setSelectedLoadings(new Set());
            setSelectedShipments(new Set());
        }
    }, [selectedVehicle?._id, fetchShipmentDetails]);

    // FIX 5: reset extra vehicles when driver changes
    const handleDriverChange = (data, onChange) => {
        onChange(data);
        setSelectedToVehicle(null);
        setExtraDriverVehicles([]);
    };

    // FIX 3: always show To Vehicle when driver selected, include extras + sentinel
    const toVehicleOptions = useMemo(() => {
        const base = selectedDriver?.assigned_vehicle_ids || [];
        return [...base, ...extraDriverVehicles, ADD_VEHICLE_SENTINEL];
    }, [selectedDriver?.assigned_vehicle_ids, extraDriverVehicles]);

    // FIX 4: intercept sentinel selection
    const handleToVehicleChange = (_, data) => {
        if (!data) {
            setSelectedToVehicle(null);
            return;
        }
        if (data._id === ADD_VEHICLE_SENTINEL._id) {
            setAddVehicleModalOpen(true);
            return;
        }
        setSelectedToVehicle(data);
    };

    const handleAddVehicleSuccess = (newVehicle) => {
        setExtraDriverVehicles((prev) => [...prev, newVehicle]);
        setSelectedToVehicle(newVehicle);
        setAddVehicleModalOpen(false);
        setToast({
            open: true,
            severity: "success",
            message: `Vehicle ${newVehicle.registration_number} created and assigned to ${selectedDriver.name}.`,
        });
    };

    const groupedByLoading = useMemo(() => {
        if (!shipmentData?.data?.shipments) return [];

        const groups = new Map();
        shipmentData.data.shipments.forEach((item) => {
            const key = item._id;
            if (!groups.has(key)) {
                groups.set(key, {
                    _id: item._id,
                    loadingId: item.loadingId,
                    loadingTime: item.loadingTime,
                    shipments: [],
                });
            }
            groups.get(key).shipments.push({
                shipmentId: item.shipmentId,
                weight: item.weight,
                unit: item.unit,
                senderName: item.senderName,
                receiverName: item.receiverName,
            });
        });

        return Array.from(groups.values());
    }, [shipmentData]);

    const handleLoadingSelect = (_id, shipments) => {
        const newSelectedLoadings = new Set(selectedLoadings);
        const newSelectedShipments = new Set(selectedShipments);

        if (selectedLoadings.has(_id)) {
            newSelectedLoadings.delete(_id);
            shipments.forEach(s => newSelectedShipments.delete(`${_id}:${s.shipmentId}`));
        } else {
            newSelectedLoadings.add(_id);
            shipments.forEach(s => newSelectedShipments.add(`${_id}:${s.shipmentId}`));
        }

        setSelectedLoadings(newSelectedLoadings);
        setSelectedShipments(newSelectedShipments);
    };

    const handleShipmentSelect = (_id, shipmentId) => {
        const key = `${_id}:${shipmentId}`;
        const newSelectedShipments = new Set(selectedShipments);
        const newSelectedLoadings = new Set(selectedLoadings);

        if (selectedShipments.has(key)) {
            newSelectedShipments.delete(key);
            newSelectedLoadings.delete(_id);
        } else {
            newSelectedShipments.add(key);
            const loading = groupedByLoading.find(g => g._id === _id);
            const allSelected = loading?.shipments.every(
                s => newSelectedShipments.has(`${_id}:${s.shipmentId}`) || s.shipmentId === shipmentId
            );
            if (allSelected) newSelectedLoadings.add(_id);
        }

        setSelectedShipments(newSelectedShipments);
        setSelectedLoadings(newSelectedLoadings);
    };

    const getLoadingCheckboxState = (_id, shipments) => {
        const selectedCount = shipments.filter(s =>
            selectedShipments.has(`${_id}:${s.shipmentId}`)
        ).length;
        if (selectedCount === 0) return { checked: false, indeterminate: false };
        if (selectedCount === shipments.length) return { checked: true, indeterminate: false };
        return { checked: false, indeterminate: true };
    };

    const selectedSummary = useMemo(() => ({
        loadingCount: selectedLoadings.size,
        shipmentCount: selectedShipments.size,
    }), [selectedLoadings, selectedShipments]);

    const handleSelectAll = () => {
        if (selectedLoadings.size === groupedByLoading.length) {
            setSelectedLoadings(new Set());
            setSelectedShipments(new Set());
        } else {
            const allLoadings = new Set(groupedByLoading.map(g => g._id));
            const allShipments = new Set();
            groupedByLoading.forEach(group => {
                group.shipments.forEach(s => {
                    allShipments.add(`${group._id}:${s.shipmentId}`);
                });
            });
            setSelectedLoadings(allLoadings);
            setSelectedShipments(allShipments);
        }
    };

    const handleTransfer = async () => {
        if (!selectedDriver?._id || !selectedToVehicle?._id || selectedLoadings.size === 0) return;

        const loadingIds = Array.from(selectedLoadings);
        const result = await partialTransferShipments({
            toDriverId: selectedDriver._id,
            fromVehicle: selectedVehicle,
            toVehicle: selectedToVehicle,
            loadingIds,
        });

        if (result?.data?.success) {
            setToast({
                open: true,
                severity: "success",
                message: `Transfer request sent to ${selectedDriver.name}. Waiting for approval.`,
            });
            setSelectedLoadings(new Set());
            setSelectedShipments(new Set());
            setSelectedToVehicle(null);
            if (selectedVehicle?._id) fetchShipmentDetails({ vehicleId: selectedVehicle._id });
        } else {
            setToast({
                open: true,
                severity: "error",
                message: result?.error?.data?.message || "Transfer failed. Please try again.",
            });
        }
    };

    return (
        <Grid container spacing={3}>

            {/* Controls Row */}
            <Grid item xs={12}>
                <Stack direction="row" spacing={2} alignItems="flex-start">

                    {/* From Vehicle */}
                    <Box sx={{ flex: 1 }}>
                        <Controller
                            name="vehicle"
                            control={control}
                            rules={{ required: "Please select a vehicle." }}
                            render={({ field: { value, onChange }, fieldState: { error } }) => (
                                <BaseAutocomplete
                                    fullWidth
                                    required
                                    label="Vehicle"
                                    value={value ?? null}
                                    onChange={(_, data) => onChange(data)}
                                    getOptionLabel={(option) =>
                                        option ? `${option.vehicle_id} - ${option.registration_number}` : ""
                                    }
                                    isOptionEqualToValue={(opt, val) => opt?._id === val?._id}
                                    options={data?.assigned_vehicle_ids || []}
                                    loading={isLoading}
                                    loadingText="Loading Vehicles..."
                                    helperText={error?.message}
                                    error={!!error}
                                />
                            )}
                        />
                    </Box>

                    {/* To Driver */}
                    <Box sx={{ flex: 1 }}>
                        <Controller
                            name="driver"
                            control={control}
                            render={({ field: { value, onChange } }) => (
                                <BaseAutocomplete
                                    fullWidth
                                    label="Transfer To Driver"
                                    value={value ?? null}
                                    onChange={(_, data) => handleDriverChange(data, onChange)}
                                    onInputChange={(_, inputValue, reason) => {
                                        if (reason === "reset") return;
                                        clearTimeout(debounceRef.current);
                                        debounceRef.current = setTimeout(() => {
                                            if (inputValue.trim()) fetchDrivers({
                                                q: inputValue.trim(),
                                                excludeId: driverIds,
                                            });
                                        }, 300);
                                    }}
                                    getOptionLabel={(option) =>
                                        option ? `${option.name} (${option.driverId})` : ""
                                    }
                                    isOptionEqualToValue={(opt, val) => opt?._id === val?._id}
                                    options={driverOptions || []}
                                    loading={isLoadingDrivers}
                                    loadingText="Searching drivers..."
                                />
                            )}
                        />
                    </Box>

                    {/* FIX 3: To Vehicle — always visible when driver selected */}
                    {selectedDriver && (
                        <Box sx={{ flex: 1 }}>
                            <BaseAutocomplete
                                fullWidth
                                label="Transfer To Vehicle"
                                value={selectedToVehicle ?? null}
                                onChange={handleToVehicleChange}          // FIX 4
                                options={toVehicleOptions}                // FIX 3
                                getOptionLabel={(option) => {
                                    if (!option) return "";
                                    if (option._id === ADD_VEHICLE_SENTINEL._id) return "+ Add Vehicle";
                                    return `${option.vehicle_id} - ${option.registration_number}`;
                                }}
                                isOptionEqualToValue={(opt, val) => opt?._id === val?._id}
                                renderOption={(props, option) => {
                                    if (option._id === ADD_VEHICLE_SENTINEL._id) {
                                        return (
                                            <li {...props} key="__ADD_VEHICLE__">
                                                <Typography variant="body2" color="primary" fontWeight={600}>
                                                    + Add Vehicle
                                                </Typography>
                                            </li>
                                        );
                                    }
                                    return (
                                        <li {...props} key={option._id}>
                                            {option.vehicle_id} - {option.registration_number}
                                        </li>
                                    );
                                }}
                            />
                        </Box>
                    )}

                    {/* Transfer Button */}
                    <Box sx={{ pt: 0.5 }}>
                        <Button
                            variant="contained"
                            onClick={handleTransfer}
                            disabled={
                                !selectedDriver?._id ||
                                !selectedToVehicle?._id ||
                                selectedLoadings.size === 0 ||
                                isTransferring
                            }
                            sx={{ whiteSpace: "nowrap", height: 40 }}
                        >
                            {isTransferring ? "Transferring..." : `Transfer Selected (${selectedSummary.shipmentCount})`}
                        </Button>
                    </Box>

                </Stack>
            </Grid>

            {/* Summary */}
            {shipmentData?.data && (
                <Grid item xs={12}>
                    <Stack direction="row" spacing={2} alignItems="center">
                        {[
                            { label: "Total Loadings", value: shipmentData.data.totalLoadings },
                            { label: "Total Shipments", value: shipmentData.data.totalShipments },
                            { label: "Total Weight", value: `${shipmentData.data.totalWeight} kg` },
                            { label: "Selected Loadings", value: selectedSummary.loadingCount, color: "primary.main" },
                            { label: "Selected Shipments", value: selectedSummary.shipmentCount, color: "primary.main" },
                        ].map(({ label, value, color }) => (
                            <Paper
                                key={label}
                                variant="outlined"
                                sx={{
                                    px: 2, py: 1, minWidth: 120,
                                    textAlign: "center", borderRadius: 0,
                                    borderColor: "divider", bgcolor: "background.paper",
                                }}
                            >
                                <Typography variant="caption" color="text.secondary" display="block">
                                    {label}
                                </Typography>
                                <Typography variant="subtitle1" fontWeight={600} color={color || "text.primary"}>
                                    {value}
                                </Typography>
                            </Paper>
                        ))}
                    </Stack>
                </Grid>
            )}

            {/* Grouped Table */}
            {isLoadingShipments ? (
                <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Loading shipments...</Typography>
                </Grid>
            ) : (
                groupedByLoading.length > 0 && (
                    <Grid item xs={12}>
                        <TableContainer
                            component={Paper}
                            variant="outlined"
                            sx={{ borderRadius: 0, borderColor: "divider", bgcolor: "background.paper" }}
                        >
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: "action.hover" }}>
                                        <TableCell padding="checkbox" sx={{ borderColor: "divider" }}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={selectedLoadings.size === groupedByLoading.length && groupedByLoading.length > 0}
                                                        indeterminate={selectedLoadings.size > 0 && selectedLoadings.size < groupedByLoading.length}
                                                        onChange={handleSelectAll}
                                                        size="small"
                                                    />
                                                }
                                                label={
                                                    <Typography variant="body2">
                                                        {selectedLoadings.size > 0 ? `${selectedLoadings.size} selected` : "Select All"}
                                                    </Typography>
                                                }
                                            />
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: "text.primary", borderColor: "divider" }}>
                                            Loading ID
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: "text.primary", borderColor: "divider" }}>
                                            Shipments
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {groupedByLoading.map(({ _id, loadingId, shipments }) => {
                                        const checkboxState = getLoadingCheckboxState(_id, shipments);
                                        return (
                                            <TableRow
                                                key={_id}
                                                sx={{ verticalAlign: "top", "&:last-child td": { borderBottom: 0 } }}
                                            >
                                                <TableCell padding="checkbox" sx={{ borderColor: "divider" }}>
                                                    <Checkbox
                                                        checked={checkboxState.checked}
                                                        indeterminate={checkboxState.indeterminate}
                                                        onChange={() => handleLoadingSelect(_id, shipments)}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ borderColor: "divider" }}>
                                                    <Typography
                                                        variant="caption"
                                                        fontWeight={600}
                                                        color="primary"
                                                        sx={{ fontFamily: "monospace" }}
                                                    >
                                                        {loadingId}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ borderColor: "divider" }}>
                                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                                        {shipments.map((shipment) => {
                                                            const shipmentKey = `${_id}:${shipment.shipmentId}`;
                                                            return (
                                                                <Box
                                                                    key={shipment.shipmentId}
                                                                    sx={{
                                                                        display: "flex", alignItems: "center", gap: 1, p: 0.5,
                                                                        bgcolor: selectedShipments.has(shipmentKey) ? "action.selected" : "transparent",
                                                                    }}
                                                                >
                                                                    <Checkbox
                                                                        checked={selectedShipments.has(shipmentKey)}
                                                                        onChange={() => handleShipmentSelect(_id, shipment.shipmentId)}
                                                                        size="small"
                                                                    />
                                                                    <Box sx={{ flex: 1 }}>
                                                                        <Typography
                                                                            variant="caption"
                                                                            sx={{
                                                                                fontFamily: "monospace",
                                                                                fontSize: "0.72rem",
                                                                                fontWeight: 600,
                                                                                color: "text.primary",
                                                                            }}
                                                                        >
                                                                            {shipment.shipmentId}
                                                                        </Typography>
                                                                        <Typography
                                                                            variant="caption"
                                                                            display="block"
                                                                            color="text.secondary"
                                                                            sx={{ fontSize: "0.65rem" }}
                                                                        >
                                                                            From: {shipment.senderName} | To: {shipment.receiverName} | {getUnitDisplay(shipment)}
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                            );
                                                        })}
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>
                )
            )}

            {/* FIX 2: Modal is a sibling, NOT inside Snackbar */}
            <AddVehicleModal
                open={addVehicleModalOpen}
                onClose={() => setAddVehicleModalOpen(false)}
                onSuccess={handleAddVehicleSuccess}
                driverId={selectedDriver?._id}
            />

            {/* Toast */}
            <Snackbar
                open={toast.open}
                autoHideDuration={4000}
                onClose={() => setToast((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
                <Alert
                    severity={toast.severity}
                    variant="filled"
                    onClose={() => setToast((prev) => ({ ...prev, open: false }))}
                >
                    {toast.message}
                </Alert>
            </Snackbar>

        </Grid>
    );
};